import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import "./PublicProfileSearch.css";

interface PublicProfileSearchProps {
  jobId?: string | number;
  onCreateApplication?: (candidateId: string | number) => void;
}

interface SearchOptions {
  country: string;
  perPage: number;
  page: number;
  [key: string]: unknown;
}

interface ProfileCandidate {
  apolloId?: string | number;
  name?: string;
  title?: string;
  company?: string;
  location?: string;
  linkedinUrl?: string;
  skills?: string[];
  summary?: string;
}

interface JobInfo {
  title: string;
  skills?: string[];
}

interface SearchResults {
  candidates?: ProfileCandidate[];
  totalResults: number;
  country?: string;
  job?: JobInfo;
}

interface SystemConfigResponse {
  data?: {
    value?: string;
  };
}

const PublicProfileSearch: React.FC<PublicProfileSearchProps> = ({
  jobId,
}) => {
  const { t } = useTranslation("recruiting");
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidates, setSelectedCandidates] = useState<
    Set<string | number>
  >(new Set());
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    country: "",
    perPage: 25,
    page: 1,
  });

  // Fetch country setting from system config
  useQuery<SystemConfigResponse>(
    ["system-config", "apollo_search_country"],
    () =>
      recruitingAPI.getSystemConfig(
        "apollo_search_country",
      ) as Promise<SystemConfigResponse>,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      onSuccess: (data) => {
        if (data?.data?.value && !searchOptions.country) {
          setSearchOptions((prev) => ({
            ...prev,
            country: data.data?.value || "",
          }));
        }
      },
    },
  );

  const searchMutation = useMutation<SearchResults, Error, SearchOptions>(
    async (options) => {
      const result = (await recruitingAPI.searchApolloForJob(
        jobId,
        options,
      )) as { data: SearchResults };
      return result.data;
    },
    {
      onSuccess: (result) => {
        setResults(result);
        setIsSearching(false);
        setError(null);
        setSelectedCandidates(new Set()); // Reset selection on new search
      },
      onError: (
        err: Error & {
          response?: { data?: { error?: string; message?: string } };
        },
      ) => {
        setIsSearching(false);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            t("apolloFinder.searchFailed"),
        );
      },
    },
  );

  const handleSearch = () => {
    setIsSearching(true);
    setError(null);
    searchMutation.mutate(searchOptions);
  };

  const handleToggleSelect = (candidateId: string | number) => {
    setSelectedCandidates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(candidateId)) {
        newSet.delete(candidateId);
      } else {
        newSet.add(candidateId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (results?.candidates) {
      const allIds = results.candidates
        .map((candidate, index) => candidate.apolloId || index)
        .filter((id): id is string | number => id !== null && id !== undefined);
      setSelectedCandidates(new Set(allIds));
    }
  };

  const handleDeselectAll = () => {
    setSelectedCandidates(new Set());
  };

  const handleOpenSelectedLinkedIn = () => {
    if (!results?.candidates || selectedCandidates.size === 0) return;

    const selectedLinkedInUrls = results.candidates
      .filter((candidate, index) => {
        const candidateId = candidate.apolloId || index;
        return selectedCandidates.has(candidateId) && candidate.linkedinUrl;
      })
      .map((candidate) => candidate.linkedinUrl)
      .filter((url): url is string => !!url);

    if (selectedLinkedInUrls.length === 0) {
      alert(t("apolloFinder.noLinkedInUrls"));
      return;
    }

    // Open each LinkedIn URL in a new tab
    selectedLinkedInUrls.forEach((url, index) => {
      // Small delay to avoid browser blocking multiple popups
      setTimeout(() => {
        window.open(url, "_blank", "noopener,noreferrer");
      }, index * 100);
    });
  };

  return (
    <div className="public-profile-search">
      <div className="finder-header">
        <h3>{t("apolloFinder.title")}</h3>
        <p className="finder-description">{t("apolloFinder.description")}</p>
      </div>

      <div className="search-controls">
        <div className="control-group">
          <label htmlFor="profile-search-country">
            {t("apolloFinder.searchCountry")}
          </label>
          <input
            id="profile-search-country"
            type="text"
            value={searchOptions.country}
            onChange={(e) =>
              setSearchOptions((prev) => ({ ...prev, country: e.target.value }))
            }
            placeholder={t("apolloFinder.countryPlaceholder")}
            disabled={isSearching}
          />
          <small>{t("apolloFinder.countryHint")}</small>
        </div>
        <div className="control-group">
          <label htmlFor="profile-search-per-page">
            {t("apolloFinder.resultsPerPage")}
          </label>
          <select
            id="profile-search-per-page"
            value={searchOptions.perPage}
            onChange={(e) =>
              setSearchOptions((prev) => ({
                ...prev,
                perPage: Number.parseInt(e.target.value, 10),
              }))
            }
            disabled={isSearching}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
        <button
          className="btn-primary"
          onClick={handleSearch}
          disabled={isSearching}
        >
          {isSearching
            ? t("apolloFinder.searching")
            : t("apolloFinder.searchButton")}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <strong>{t("apolloFinder.error")}</strong> {error}
        </div>
      )}

      {results && (
        <div className="finder-results">
          <div className="results-header">
            <div className="results-header-top">
              <p>
                {t("apolloFinder.foundResults", {
                  count: results.totalResults,
                })}
                {results.country &&
                  ` ${t("apolloFinder.inCountry", { country: results.country })}`}
              </p>
              {selectedCandidates.size > 0 && (
                <p className="selected-count">
                  {t("apolloFinder.selected", {
                    count: selectedCandidates.size,
                  })}
                </p>
              )}
            </div>
            {results.job && (
              <p className="job-info">
                {t("apolloFinder.searchingFor")}{" "}
                <strong>{results.job.title}</strong>
                {results.job.skills && results.job.skills.length > 0 && (
                  <span>
                    {" "}
                    {t("apolloFinder.skills")} {results.job.skills.join(", ")}
                  </span>
                )}
              </p>
            )}
            {results.candidates && results.candidates.length > 0 && (
              <div className="results-actions">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={
                    selectedCandidates.size === results.candidates.length
                      ? handleDeselectAll
                      : handleSelectAll
                  }
                >
                  {selectedCandidates.size === results.candidates.length
                    ? t("apolloFinder.deselectAll")
                    : t("apolloFinder.selectAll")}
                </button>
                <button
                  className="btn-primary btn-sm"
                  onClick={handleOpenSelectedLinkedIn}
                  disabled={selectedCandidates.size === 0}
                >
                  {t("apolloFinder.openLinkedIn", {
                    count: selectedCandidates.size,
                  })}
                </button>
              </div>
            )}
          </div>

          <div className="candidates-list">
            {results.candidates && results.candidates.length > 0 ? (
              results.candidates.map((candidate, index) => {
                const candidateId = candidate.apolloId || index;
                const isSelected = selectedCandidates.has(candidateId);

                return (
                  <div
                    key={candidateId}
                    className={`profile-card ${isSelected ? "selected" : ""}`}
                  >
                    <div className="candidate-card-content">
                      <div className="candidate-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(candidateId)}
                          id={`candidate-${candidateId}`}
                        />
                        <label htmlFor={`candidate-${candidateId}`}>
                          <span className="sr-only">
                            {t("apolloFinder.selectCandidate", {
                              name: candidate.name || "candidate",
                            })}
                          </span>
                        </label>
                      </div>
                      <div className="candidate-main-info">
                        <div className="candidate-name-row">
                          <h4>{candidate.name || t("apolloFinder.unknown")}</h4>
                          {candidate.linkedinUrl && (
                            <a
                              href={candidate.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="linkedin-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t("apolloFinder.linkedIn")}
                            </a>
                          )}
                        </div>
                        {candidate.title && (
                          <div className="candidate-title">
                            {candidate.title}
                            {candidate.company &&
                              ` ${t("apolloFinder.titleAtCompany", { company: candidate.company })}`}
                          </div>
                        )}
                        <div className="candidate-location">
                          {candidate.location || t("apolloFinder.noLocation")}
                        </div>
                        {candidate.skills && candidate.skills.length > 0 && (
                          <div className="candidate-skills">
                            <div className="skills-tags">
                              {candidate.skills.slice(0, 8).map((skill) => (
                                <span
                                  key={`skill-${skill}`}
                                  className="skill-tag"
                                >
                                  {skill}
                                </span>
                              ))}
                              {candidate.skills.length > 8 && (
                                <span className="skill-tag-more">
                                  {t("apolloFinder.moreSkills", {
                                    count: candidate.skills.length - 8,
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {candidate.summary && (
                          <div className="candidate-summary">
                            {candidate.summary.length > 150
                              ? `${candidate.summary.substring(0, 150)}...`
                              : candidate.summary}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-candidates">
                <p>{t("apolloFinder.noCandidates")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!results && !isSearching && (
        <div className="finder-info">
          <p>{t("apolloFinder.infoText")}</p>
          <p className="info-note">
            {t("apolloFinder.apiKeyNote")}
          </p>
        </div>
      )}
    </div>
  );
};

export default PublicProfileSearch;
